import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  CONTACT_RELATIONS,
  DEFAULT_REGISTRATION_CONTACTS,
  type ContactRelation,
  type FormField,
  type MemberContact,
  type PublicSeasonInfo,
  type RegistrationBlock,
} from '@publiqa/shared'
import { ApiError, upload } from '../../lib/api'
import { lines } from '../../lib/richtext'
import { useFetch } from '../../lib/useFetch'
import { usePublic, usePubliqApi } from '../context'
import { SectionHead } from './Blocks'
import { useRevealOnLink } from './reveal'
import './registration.css'

/**
 * Bulletin d'inscription en ligne : replié derrière « Remplir le bulletin en
 * ligne », puis présenté en étapes (les champs « Titre d'étape » du
 * back-office découpent le bulletin), avec une étape « Contacts ».
 */

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.odt'
const MAX_MB = 8
const MAX_CONTACTS = 6

/** « 2026-09-30 » → « 30 septembre 2026 ». */
function longDate(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

type Step = { title: string; fields: FormField[]; contacts?: boolean }

/** Découpe le bulletin en étapes ; l'étape « Contacts » vient après la première. */
function buildSteps(fields: FormField[], contactsTitle: string | null): Step[] {
  const steps: Step[] = []
  let current: Step | null = null
  for (const f of fields) {
    if (f.type === 'section') {
      current = { title: f.label, fields: [] }
      steps.push(current)
    } else {
      if (!current) {
        current = { title: 'Le bulletin', fields: [] }
        steps.push(current)
      }
      current.fields.push(f)
    }
  }
  const kept = steps.filter((s) => s.fields.length)
  if (contactsTitle)
    kept.splice(Math.min(1, kept.length), 0, { title: contactsTitle, fields: [], contacts: true })
  return kept
}

const blankContact = (relation: ContactRelation = 'mere'): MemberContact => ({
  relation,
  relationOther: '',
  firstName: '',
  lastName: '',
  phone: '',
  phone2: '',
  email: '',
  legalGuardian: false,
  emergency: true,
  pickup: false,
})

/** Une liste courte d'options courtes s'affiche en pastilles plutôt qu'en liste. */
const asChips = (options: string[]) =>
  options.length > 0 && options.length <= 6 && options.every((o) => o.length <= 28)

// ───────────────────────────────────────────────────────────── champs

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

/**
 * Date en trois parties (jour, mois en toutes lettres, année) : plus simple
 * qu'un calendrier pour une date de naissance, et identique sur tous les
 * appareils. La valeur part en « AAAA-MM-JJ » dans un champ caché.
 */
function DateField({ f, error, foot }: { f: FormField; error?: string; foot: ReactNode }) {
  const id = `rg-${f.id}`
  const [d, setD] = useState('')
  const [m, setM] = useState('')
  const [y, setY] = useState('')
  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLSelectElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)
  const birth = /naissance/i.test(f.label)
  const thisYear = new Date().getFullYear()

  const complete = d !== '' && m !== '' && y.length === 4
  const iso = complete ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : ''
  const date = iso ? new Date(`${iso}T12:00:00`) : null
  const real = !!date && date.getDate() === Number(d) && date.getMonth() + 1 === Number(m)
  const future = real && date! > new Date()
  const problem =
    !d && !m && !y
      ? f.required
        ? 'Indiquez la date.'
        : ''
      : !complete
        ? 'Date incomplète : jour, mois et année (4 chiffres).'
        : !real
          ? 'Cette date n’existe pas.'
          : birth && future
            ? 'La date de naissance est dans le futur.'
            : ''
  // Le contrôle de l'étape (checkValidity) s'appuie sur le champ « jour ».
  useEffect(() => {
    dayRef.current?.setCustomValidity(problem)
  }, [problem])

  const age =
    birth && real && !future
      ? (() => {
          const now = new Date()
          let a = now.getFullYear() - date!.getFullYear()
          if (now < new Date(now.getFullYear(), date!.getMonth(), date!.getDate())) a--
          return a
        })()
      : null

  return (
    <fieldset className={`rg-cell rg-choice rg-date${error ? ' has-error' : ''}`}>
      <legend>
        {f.label}
        {f.required && (
          <span className="rg-req" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {age !== null && <span className="rg-age"> · {age} ans</span>}
      </legend>
      <div className="rg-date-parts">
        <input
          ref={dayRef}
          id={id}
          inputMode="numeric"
          autoComplete={birth ? 'bday-day' : 'off'}
          placeholder="JJ"
          aria-label={`${f.label} — jour`}
          maxLength={2}
          value={d}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 2)
            setD(v)
            if (v.length === 2 || Number(v) > 3) monthRef.current?.focus()
          }}
        />
        <select
          ref={monthRef}
          aria-label={`${f.label} — mois`}
          autoComplete={birth ? 'bday-month' : 'off'}
          value={m}
          onChange={(e) => {
            setM(e.target.value)
            if (e.target.value) yearRef.current?.focus()
          }}
        >
          <option value="">Mois</option>
          {MONTHS.map((name, i) => (
            <option key={name} value={String(i + 1)}>
              {name}
            </option>
          ))}
        </select>
        <input
          ref={yearRef}
          inputMode="numeric"
          autoComplete={birth ? 'bday-year' : 'off'}
          placeholder="AAAA"
          aria-label={`${f.label} — année`}
          maxLength={4}
          value={y}
          onChange={(e) => setY(e.target.value.replace(/\D/g, '').slice(0, 4))}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !y) monthRef.current?.focus()
          }}
        />
      </div>
      <input type="hidden" name={f.id} value={real ? iso : ''} />
      {birth && !problem && !complete && (
        <p className="rg-hint">Par exemple : 12 · mars · {thisYear - 10}</p>
      )}
      {foot}
    </fieldset>
  )
}

/**
 * Photo du futur nageur : le parent la prend sur le moment ou la choisit dans
 * ses photos ; aperçu immédiat. Elle deviendra la photo de sa fiche au club.
 */
function PhotoInput({
  common,
  label,
  foot,
  error,
}: {
  common: { id: string; name: string; required: boolean }
  label: ReactNode
  foot: ReactNode
  error?: string
}) {
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview)
    },
    [preview],
  )
  return (
    <div className={`rg-cell rg-wide${error ? ' has-error' : ''}`}>
      <label className={`rg-photo${preview ? ' has-photo' : ''}`} htmlFor={common.id}>
        <input
          type="file"
          // Sans HEIC : l'iPhone convertit alors la photo en JPEG.
          accept="image/jpeg,image/png,image/webp"
          {...common}
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.setCustomValidity(
              file && file.size > MAX_MB * 1024 * 1024
                ? `Photo trop lourde (${MAX_MB} Mo maximum).`
                : '',
            )
            setPreview(file ? URL.createObjectURL(file) : null)
          }}
        />
        <span className="rg-photo-frame" aria-hidden={!preview}>
          {preview ? (
            <img src={preview} alt="Aperçu de la photo" />
          ) : (
            <svg viewBox="0 0 48 48" aria-hidden="true">
              <circle cx="24" cy="18" r="8" />
              <path d="M8 42c2-8.5 8.5-13 16-13s14 4.5 16 13" />
            </svg>
          )}
        </span>
        <span className="rg-drop-text">
          <strong>{label}</strong>
          <small>
            {preview
              ? 'Touchez pour changer de photo.'
              : 'Une photo récente, visage bien visible : prenez-la maintenant ou choisissez-la dans vos photos.'}
          </small>
        </span>
      </label>
      {foot}
    </div>
  )
}

function Field({ f, error }: { f: FormField; error?: string }) {
  const id = `rg-${f.id}`
  const [fileName, setFileName] = useState('')
  const describedBy = error ? `${id}-err` : f.placeholder ? `${id}-hint` : undefined
  const common = {
    id,
    name: f.id,
    required: f.required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  }
  const label = (
    <>
      {f.label}
      {f.required && (
        <span className="rg-req" aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </>
  )
  const foot = (
    <>
      {error ? (
        <p className="rg-error" id={`${id}-err`}>
          {error}
        </p>
      ) : (
        f.placeholder &&
        f.type !== 'text' &&
        f.type !== 'email' &&
        f.type !== 'tel' && (
          <p className="rg-hint" id={`${id}-hint`}>
            {f.placeholder}
          </p>
        )
      )}
    </>
  )

  if (f.type === 'checkbox')
    return (
      <div className={`rg-cell rg-wide${error ? ' has-error' : ''}`}>
        <label className="rg-switch">
          <input type="checkbox" value="1" {...common} />
          <span className="rg-switch-ui" aria-hidden="true" />
          <span>{label}</span>
        </label>
        {foot}
      </div>
    )

  if (f.type === 'photo')
    return <PhotoInput common={common} label={label} foot={foot} error={error} />

  if (f.type === 'file')
    return (
      <div className={`rg-cell rg-wide${error ? ' has-error' : ''}`}>
        <label className={`rg-drop${fileName ? ' has-file' : ''}`} htmlFor={id}>
          <input
            type="file"
            accept={ACCEPT}
            {...common}
            onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')}
          />
          <span className="rg-drop-icon" aria-hidden="true">
            {fileName ? '✓' : '↑'}
          </span>
          <span className="rg-drop-text">
            <strong>{label}</strong>
            <small>
              {fileName ||
                `Glissez le fichier ici ou touchez pour choisir — PDF, image ou Word, ${MAX_MB} Mo max.`}
            </small>
          </span>
        </label>
        {foot}
      </div>
    )

  if (f.type === 'select') {
    const options = lines(f.options)
    if (asChips(options))
      return (
        <fieldset
          className={`rg-cell rg-choice${options.length > 3 ? ' rg-wide' : ''}${error ? ' has-error' : ''}`}
        >
          <legend>{label}</legend>
          <div className="rg-chips">
            {options.map((o) => (
              <label key={o}>
                <input type="radio" name={f.id} value={o} required={f.required} />
                <span>{o}</span>
              </label>
            ))}
          </div>
          {foot}
        </fieldset>
      )
    return (
      <div className={`rg-cell rg-float rg-static${error ? ' has-error' : ''}`}>
        <select defaultValue="" {...common}>
          <option value="" disabled>
            Choisir…
          </option>
          {options.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
        <label htmlFor={id}>{label}</label>
        {foot}
      </div>
    )
  }

  if (f.type === 'date') return <DateField f={f} error={error} foot={foot} />

  if (f.type === 'textarea')
    return (
      <div className={`rg-cell rg-wide rg-float${error ? ' has-error' : ''}`}>
        <textarea rows={4} placeholder=" " {...common} />
        <label htmlFor={id}>{label}</label>
        {foot}
      </div>
    )

  return (
    <div
      className={`rg-cell rg-float${/adresse/i.test(f.label) ? ' rg-wide' : ''}${error ? ' has-error' : ''}`}
    >
      <input
        type={f.type}
        placeholder=" "
        autoComplete={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : undefined}
        {...common}
      />
      <label htmlFor={id}>{label}</label>
      {foot}
    </div>
  )
}

// ───────────────────────────────────────────────────────────── contacts

function ContactCard({
  c,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  c: MemberContact
  index: number
  canRemove: boolean
  onChange: (c: MemberContact) => void
  onRemove: () => void
}) {
  const set = (p: Partial<MemberContact>) => onChange({ ...c, ...p })
  const id = (k: string) => `rg-c${index}-${k}`
  return (
    <article className="rg-contact">
      <header>
        <span className="rg-contact-n">{index + 1}</span>
        <strong>Contact {index + 1}</strong>
        {canRemove && (
          <button
            type="button"
            className="rg-icon"
            onClick={onRemove}
            aria-label={`Retirer le contact ${index + 1}`}
          >
            ✕
          </button>
        )}
      </header>
      <fieldset className="rg-choice rg-wide">
        <legend>
          Lien avec le nageur
          <span className="rg-req" aria-hidden="true">
            {' '}
            *
          </span>
        </legend>
        <div className="rg-chips rg-chips-sm">
          {(Object.keys(CONTACT_RELATIONS) as ContactRelation[]).map((r) => (
            <label key={r}>
              <input
                type="radio"
                name={id('relation')}
                checked={c.relation === r}
                onChange={() => set({ relation: r })}
              />
              <span>{CONTACT_RELATIONS[r]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="rg-grid">
        {c.relation === 'autre' && (
          <div className="rg-cell rg-float rg-wide">
            <input
              id={id('other')}
              placeholder=" "
              required
              value={c.relationOther}
              onChange={(e) => set({ relationOther: e.target.value })}
            />
            <label htmlFor={id('other')}>
              Précisez le lien (grand-parent, voisin…)
              <span className="rg-req" aria-hidden="true">
                {' '}
                *
              </span>
            </label>
          </div>
        )}
        <div className="rg-cell rg-float">
          <input
            id={id('first')}
            placeholder=" "
            autoComplete="off"
            value={c.firstName}
            onChange={(e) => set({ firstName: e.target.value })}
          />
          <label htmlFor={id('first')}>Prénom</label>
        </div>
        <div className="rg-cell rg-float">
          <input
            id={id('last')}
            placeholder=" "
            autoComplete="off"
            required
            value={c.lastName}
            onChange={(e) => set({ lastName: e.target.value })}
          />
          <label htmlFor={id('last')}>
            Nom
            <span className="rg-req" aria-hidden="true">
              {' '}
              *
            </span>
          </label>
        </div>
        <div className="rg-cell rg-float">
          <input
            id={id('phone')}
            type="tel"
            placeholder=" "
            required
            minLength={6}
            value={c.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
          <label htmlFor={id('phone')}>
            Téléphone
            <span className="rg-req" aria-hidden="true">
              {' '}
              *
            </span>
          </label>
        </div>
        <div className="rg-cell rg-float">
          <input
            id={id('phone2')}
            type="tel"
            placeholder=" "
            value={c.phone2}
            onChange={(e) => set({ phone2: e.target.value })}
          />
          <label htmlFor={id('phone2')}>Autre téléphone</label>
        </div>
        <div className="rg-cell rg-float rg-wide">
          <input
            id={id('email')}
            type="email"
            placeholder=" "
            value={c.email}
            onChange={(e) => set({ email: e.target.value })}
          />
          <label htmlFor={id('email')}>E-mail</label>
        </div>
      </div>
      <div className="rg-flags">
        {(
          [
            ['legalGuardian', 'Responsable légal'],
            ['emergency', 'À prévenir en cas d’urgence'],
            ['pickup', 'Autorisé·e à venir chercher le nageur'],
          ] as const
        ).map(([k, text]) => (
          <label key={k} className="rg-switch rg-switch-sm">
            <input
              type="checkbox"
              checked={c[k]}
              onChange={(e) => set({ [k]: e.target.checked })}
            />
            <span className="rg-switch-ui" aria-hidden="true" />
            <span>{text}</span>
          </label>
        ))}
      </div>
    </article>
  )
}

// ───────────────────────────────────────────────────────────── assistant

function Wizard({
  b,
  endpoint,
  seasonLabel,
  onClose,
}: {
  b: RegistrationBlock
  endpoint: string
  seasonLabel: string
  onClose: () => void
}) {
  const { inert } = usePublic()
  const settings = b.contacts ?? DEFAULT_REGISTRATION_CONTACTS
  const steps = useMemo(
    () => buildSteps(b.fields, settings.enabled ? settings.title || 'Contacts' : null),
    [b.fields, settings.enabled, settings.title],
  )
  const form = useRef<HTMLFormElement>(null)
  // Temps passé sur le bulletin : distingue une personne d'un robot (voir l'API).
  const shownAt = useRef(Date.now())
  const box = useRef<HTMLDivElement>(null)
  const [step, setStep] = useState(0)
  const [formKey, setFormKey] = useState(0)
  const [contacts, setContacts] = useState<MemberContact[]>(() =>
    settings.min > 0 ? [blankContact()] : [],
  )
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const last = step === steps.length - 1
  const pct = Math.round(((state === 'done' ? steps.length : step) / steps.length) * 100)

  // Chaque changement d'étape : en haut du bulletin, focus sur le premier champ.
  useEffect(() => {
    const el = box.current
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - 90
    if (el.getBoundingClientRect().top < 0) window.scrollTo({ top, behavior: 'instant' })
    el.querySelector<HTMLElement>(
      `[data-step="${step}"] input:not([type=hidden]), [data-step="${step}"] select, [data-step="${step}"] textarea`,
    )?.focus({ preventScroll: true })
  }, [step])
  useEffect(() => {
    if (state === 'done') box.current?.querySelector<HTMLElement>('.rg-done')?.focus()
  }, [state])

  /** Vérifie l'étape affichée (contrôles natifs du navigateur + nombre de contacts). */
  function validStep(i: number): boolean {
    const el = form.current?.querySelector(`[data-step="${i}"]`)
    if (!el) return true
    const bad = [...el.querySelectorAll<HTMLInputElement>('input, select, textarea')].find(
      (x) => !x.checkValidity(),
    )
    if (bad) {
      // Champs en erreur soulignés sur cette étape seulement (pas avant d'avoir essayé).
      el.setAttribute('data-tried', '')
      bad.reportValidity()
      return false
    }
    if (steps[i]?.contacts && contacts.length < settings.min) {
      setError(
        settings.min > 1
          ? `Ajoutez au moins ${settings.min} contacts.`
          : 'Ajoutez au moins un contact.',
      )
      return false
    }
    // Pièces jointes trop lourdes : refusées avant l'envoi.
    for (const input of el.querySelectorAll<HTMLInputElement>('input[type=file]')) {
      const file = input.files?.[0]
      if (file && file.size > MAX_MB * 1024 * 1024) {
        setFieldErrors({
          ...fieldErrors,
          [input.name]: `Fichier trop lourd (${MAX_MB} Mo maximum).`,
        })
        input.focus()
        return false
      }
    }
    el.removeAttribute('data-tried')
    setError('')
    return true
  }

  function next() {
    if (validStep(step)) setStep((s) => Math.min(s + 1, steps.length - 1))
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!last) return next()
    if (inert || !validStep(step)) return
    const data = new FormData(e.currentTarget)
    data.set('_t', String(Date.now() - shownAt.current))
    for (const [k, v] of [...data.entries()]) if (v instanceof File && !v.size) data.delete(k)
    data.set('_contacts', JSON.stringify(contacts))
    setState('sending')
    setError('')
    setFieldErrors({})
    try {
      await upload(endpoint, data, setProgress)
      setState('done')
    } catch (err) {
      setState('idle')
      if (err instanceof ApiError) {
        setError(err.message)
        setFieldErrors(err.fields)
        // Ramène à l'étape du premier champ refusé.
        const firstBad = Object.keys(err.fields)[0]
        const at = steps.findIndex((s) =>
          firstBad === '_contacts' ? s.contacts : s.fields.some((f) => f.id === firstBad),
        )
        if (at >= 0) setStep(at)
      } else setError('Envoi impossible. Vérifiez votre connexion et réessayez.')
    }
  }

  function restart() {
    setFormKey((k) => k + 1)
    form.current?.reset()
    setContacts(settings.min > 0 ? [blankContact()] : [])
    setStep(0)
    setState('idle')
  }

  if (state === 'done')
    return (
      <div className="rg-card" ref={box}>
        <div className="rg-done" tabIndex={-1} role="status">
          <span className="rg-done-icon" aria-hidden="true">
            ✓
          </span>
          <h3>Bulletin envoyé</h3>
          <p>{b.successMessage}</p>
          <div className="rg-actions">
            <button type="button" className="rg-btn rg-btn-ghost" onClick={restart}>
              Inscrire une autre personne
            </button>
            <button type="button" className="rg-btn rg-btn-ghost" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    )

  return (
    <div className="rg-card" ref={box}>
      <header className="rg-head">
        <div className="rg-head-top">
          <span className="rg-kicker">Bulletin {seasonLabel}</span>
          <span className="rg-count">
            Étape {step + 1} / {steps.length}
          </span>
        </div>
        <div
          className="rg-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label="Progression"
        >
          <span style={{ width: `${Math.max(pct, 4)}%` }} />
        </div>
        <ol className="rg-steps">
          {steps.map((s, i) => (
            <li
              key={i}
              className={i === step ? 'on' : i < step ? 'done' : ''}
              aria-current={i === step ? 'step' : undefined}
            >
              <button
                type="button"
                disabled={i > step}
                onClick={() => i < step && setStep(i)}
                aria-label={`Étape ${i + 1} : ${s.title}`}
              >
                <span className="rg-dot">{i < step ? '✓' : i + 1}</span>
                <span className="rg-step-name">{s.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </header>

      <form
        key={formKey}
        ref={form}
        className="rg-form"
        onSubmit={(e) => void submit(e)}
        noValidate
      >
        {steps.map((s, i) => (
          <section
            key={i}
            data-step={i}
            className="rg-step"
            hidden={i !== step}
            aria-label={s.title}
          >
            <h3 className="rg-step-title">{s.title}</h3>
            {s.contacts ? (
              <>
                {settings.intro && <p className="rg-intro">{settings.intro}</p>}
                <div className="rg-contacts">
                  {contacts.map((c, j) => (
                    <ContactCard
                      key={j}
                      c={c}
                      index={j}
                      canRemove={contacts.length > settings.min}
                      onChange={(nc) => setContacts(contacts.map((x, k) => (k === j ? nc : x)))}
                      onRemove={() => setContacts(contacts.filter((_, k) => k !== j))}
                    />
                  ))}
                </div>
                {contacts.length < MAX_CONTACTS && (
                  <button
                    type="button"
                    className="rg-add"
                    onClick={() =>
                      setContacts([...contacts, blankContact(contacts.length ? 'pere' : 'mere')])
                    }
                  >
                    <span aria-hidden="true">+</span> Ajouter un contact
                  </button>
                )}
              </>
            ) : (
              <div className="rg-grid">
                {s.fields.map((f) => (
                  <Field key={f.id} f={f} error={fieldErrors[f.id]} />
                ))}
              </div>
            )}
          </section>
        ))}

        {/* Pot de miel anti-robots : invisible et hors tabulation. */}
        <div className="hp" aria-hidden="true">
          <label>
            Ne pas remplir <input type="text" name="_hp" tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        {error && (
          <p className="rg-alert" role="alert">
            {error}
          </p>
        )}

        <footer className="rg-foot">
          {step > 0 ? (
            <button type="button" className="rg-btn rg-btn-ghost" onClick={() => setStep(step - 1)}>
              ← Précédent
            </button>
          ) : (
            <button type="button" className="rg-btn rg-btn-ghost" onClick={onClose}>
              Annuler
            </button>
          )}
          {last ? (
            <button type="submit" className="rg-btn rg-btn-primary" disabled={state === 'sending'}>
              {state === 'sending' ? `Envoi… ${progress ? `${progress} %` : ''}` : b.submitLabel}
              {state === 'sending' && (
                <span className="rg-btn-progress" style={{ width: `${progress}%` }} />
              )}
            </button>
          ) : (
            <button type="button" className="rg-btn rg-btn-primary" onClick={next}>
              Continuer <span aria-hidden="true">→</span>
            </button>
          )}
        </footer>
      </form>
    </div>
  )
}

// ───────────────────────────────────────────────────────────── bloc

/**
 * Inscription en ligne : le bloc n'apparaît que si une saison est OUVERTE
 * (et sa date de clôture non dépassée). Le bulletin reste replié tant que
 * l'internaute n'a pas cliqué sur « Remplir le bulletin en ligne ».
 */
export function RegistrationView({ b, pageId }: { b: RegistrationBlock; pageId: string }) {
  const { publication, inert } = usePublic()
  const path = usePubliqApi()
  const { data, loading } = useFetch<PublicSeasonInfo>(path('/season'))
  // Bloc masqué : il n'apparaît (bulletin ouvert) qu'au clic sur un lien qui
  // y mène — « Remplir le bulletin d'adhésion », « S'inscrire »… — ou à
  // l'arrivée sur la page avec son ancre. Toujours visible dans l'éditeur.
  const season = data?.season ?? null
  const { open, close } = useRevealOnLink(b.id, !inert, !!season)
  if (!inert && (!open || loading || !season)) return null
  const label = season?.label ?? ''

  return (
    <section className="b-section b-form b-registration rg">
      <div className="container narrow">
        <SectionHead title={b.title} intro={b.intro} />
        <p className="rg-open">
          <span className="rg-pulse" aria-hidden="true" /> Inscriptions ouvertes
          {label ? ` — saison ${label}` : ''}
          {season?.closesOn ? `, jusqu’au ${longDate(season.closesOn)}` : ''}
        </p>
        <Wizard
          b={b}
          seasonLabel={label}
          endpoint={`/public/p/${publication.slug}/registrations/${pageId}/${b.id}`}
          onClose={close}
        />
      </div>
    </section>
  )
}
